/**
 * AppointmentsBook — the Appointments process home (route `appointments`,
 * rail "Appointments"): a location-scoped DAY BOOK of booked appointments.
 *
 * 2026-08-06 rebuild (operator: "die Einrückung ist nicht schön und es werden
 * zu wenig Daten angezeigt … Actions eher in ein Action-Menu … eine mächtige
 * Filterkomponente, idealerweise eine geshared bestehende … Location-Select
 * unsere Select-Komponente … UTM hinter Tooltip oder 'mehr Informationen'"):
 *
 *  · One 24px gutter for every band (the old screen sat at 32px while the page
 *    header sat at 24px — that was the visible mis-indent).
 *  · A dense TABLE instead of three-field rows: time+length, customer with
 *    contact, service+channel, staff, location, booking source (UTM behind a
 *    tooltip), answered booking questions, status.
 *  · Row actions collapse into the shared kebab (`RowActions`) — no inline
 *    button cluster.
 *  · Filtering runs through the shared `FilterToolbar` composite (free text +
 *    multi-select facets with counts + removable chips), which this screen is
 *    the first consumer of; the location scope is the design-system `Dropdown`,
 *    not a browser <select>.
 *  · The full record — every answer, the whole attribution block, notes — lives
 *    in a `<Drawer>` (overlay standard: one record ⇒ Drawer).
 *
 * Data comes from ONE listAppointments read (2026-09-15): the Range control
 * sends `horizonDays`, and the SERVER resolves the window from the company's
 * own today (the browser's clock is the viewer's, not the company's). The
 * response names the window it covers (`dateFrom`/`dateTo`) and the company
 * zone anchoring it (`listTimeZone`); the screen shows those bounds and
 * exports exactly them, with the filters of the read that produced the rows on
 * screen. Each row's date and times stay on the row's own clock
 * (`clockTimeZone`), so a row's "Today" is today in that zone, falling back to
 * the list zone. The location scope, the selected facets and the debounced
 * search go to the server, and the response carries the filtered total, facets
 * over the unfiltered scope and the unfiltered summary. The query is re-read
 * when the window regains focus or becomes visible (the company's day may have
 * turned). Once rows are on screen they stay through every later read, and a
 * read that fails says so beside them with Retry. Sorting, paging and row
 * clicks stay client-side and never refetch. Status model: booked → confirmed
 * → checked-in → served / no-show / cancelled / rescheduled (APT-03).
 */
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { Button, DayStrip, Skeleton, Dropdown, Icon, Pagination, T, Tooltip } from "@v0y4ge/jrni-ui";
import { useT } from "@v0y4ge/i18n-runtime";
import { Screen, PageHeader } from "../../shell/chrome";
import { useNav } from "../../shell/router";
import { Drawer } from "../../shell/Overlay";
import { DataSourceStrip } from "../../shell/opSource";
import { StatTile, EmptyState, LoadState, RowActions, FilterToolbar, type FilterFacet } from "../../shell/composites";
import { thStyle, tdStyle } from "../events/EventCard";
import { useIsMobile } from "../../shell/useResponsive";
import { toast } from "../../shell/toast";
import { can, getSessionEpoch } from "../../shell/access";
import { calendarDateKey, friendlyDateShort, wallClockToIso } from "../../lib/datetime";
import { exportAdminAppointments } from "@v0y4ge/api-client";
import { useAppointmentSlots } from "./appointment-slot-days";
import BookAppointmentSheet from "./BookAppointmentSheet";
import {
  APPT_ALL_LOCATIONS,
  apptExportParams,
  cancelAppointment, checkInAppointment, listAppointments,
  markAppointmentNoShow, rescheduleAppointment,
  type Appointment, type AppointmentsListResponse, type ApptFacetId, type ApptFacetOption,
  type ApptSlot, type ApptStatus, type ListAppointmentsParams, OPEN_APPT_STATUSES } from "./appointments-api";
import { canViewAsCustomer, openManagePreview } from "./manage-preview";

const STATUS_META: Record<ApptStatus, { label: string; labelKey: string; fg: string; bg: string }> = {
  booked:        { label: "Booked", labelKey: "appointments.status.booked.label",       fg: "#4B5563", bg: "#F3F4F6" },
  confirmed:     { label: "Confirmed", labelKey: "appointments.status.confirmed.label",    fg: "#1D4ED8", bg: "#EFF4FF" },
  "checked-in":  { label: "Checked in", labelKey: "appointments.status.checked_in.label",   fg: "#027D47", bg: "#EAF7F0" },
  served:        { label: "Served", labelKey: "appointments.status.served.label",       fg: "#374151", bg: "#EDEFF2" },
  "no-show":     { label: "No-show", labelKey: "appointments.status.no_show.label",      fg: "#B45309", bg: "#FEF4E6" },
  cancelled:     { label: "Cancelled", labelKey: "appointments.status.cancelled.label",    fg: "#B91C1C", bg: "#FDECEC" },
  rescheduled:   { label: "Rescheduled", labelKey: "appointments.status.rescheduled.label",  fg: "#6D28D9", bg: "#F2EEFC" },
};
const CHANNEL_LABEL: Record<string, string> = { inPerson: "In person", phone: "Phone", video: "Video" };
const VIA_LABEL: Record<string, string> = { website: "Website", mobile: "Mobile app", phone: "Phone", email: "Email", staff: "Booked by staff", queue: "From a queue", event: "From an event", journey: "Self-service", import: "Imported" };
/* Shared with the other appointment screen (CC 0254): "rescheduled" counts as open. */
const OPEN_STATUSES = OPEN_APPT_STATUSES;
const ALL_LOCATIONS = APPT_ALL_LOCATIONS;
/** Search keystrokes settle this long before the range read goes out. */
const QUERY_DEBOUNCE_MS = 300;
/** Focus and visibilitychange both fire when the tab comes back; one re-read is enough. */
const WAKE_REFRESH_MIN_MS = 1000;
/** Toolbar facet id → the range read's repeated filter param. `location` is
 *  not here: it drives the location scope, not a filter. */
const FACET_PARAM: Partial<Record<ApptFacetId, keyof ListAppointmentsParams>> = {
  status: "status", staff: "staffId", service: "serviceId", channel: "channel", via: "via", campaign: "campaign", question: "question",
};
const minutesOf = (a: Appointment) => {
  if (a.durationMin) return a.durationMin;
  const [sh, sm] = a.start.split(":").map(Number);
  const [eh, em] = (a.end || a.start).split(":").map(Number);
  return Math.max(0, (eh * 60 + em) - (sh * 60 + sm));
};
/** One header-cell style for the whole table — sentence case, never uppercase
 *  (the mixed casing came from a row-level text-transform that the sortable
 *  header buttons did not inherit). */
const thBase: CSSProperties = { ...thStyle, background: T.n6, color: T.soft, fontSize: 12, fontWeight: 600, textTransform: "none", letterSpacing: 0, whiteSpace: "nowrap" };

/* ── Reschedule (Drawer: a list that scrolls — overlay standard) ─────────── */
/** Exported so the record page can offer the same flow without a second copy. */
export function RescheduleDrawer({ appt, onClose, onDone }: { appt: Appointment; onClose: () => void; onDone: (a: Appointment) => void }) {
  const t = useT();
  const { date, setDate, days, timezone, slots, error: slotError, retry } = useAppointmentSlots({ enabled: true, scope: appt.id, service: appt.service, locationId: appt.locationId, initialDate: appt.date });
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savingSlot, setSavingSlot] = useState<ApptSlot | null>(null);
  /* A slot is chosen first and moved only on "Move appointment" (CC 0280): a stray click used to
     reschedule at once. A new day clears the choice. */
  const [picked, setPicked] = useState<ApptSlot | null>(null);
  useEffect(() => { setPicked(null); }, [date]);
  const pickSlot = async (s: ApptSlot) => {
    if (saving || !slots?.includes(s)) return;
    setSavingSlot(s); setSaving(true); setErr(null);
    try { const a = await rescheduleAppointment(appt.id, { date, start: s.start, end: s.end, staffId: s.staffId, staffName: s.staffName }); onDone(a); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); setSaving(false); }
  };
  return (
    <Drawer panel="appt-reschedule" testid="appt-reschedule-drawer" width={460} onClose={onClose}
      icon={<Icon.calendar size={16} />}
      title={t("appointments.book.title", "{arg1} · {name}", { arg1: t("appointments.book.reschedule", "Reschedule"), name: appt.customer.name })}
      sub={t("appointments.book.sub", "{service} — {arg2} {date} {start}", { service: appt.service, arg2: t("appointments.book.currently", "currently"), date: appt.date, start: appt.start })}>
      {timezone ? <><DayStrip days={days.map((d, i) => ({ value: d, label: i === 0 ? t("appointments.book.today", "Today") : d.slice(5), disabled: saving }))} value={date} onChange={setDate} /><div style={{ fontSize: 12, color: T.soft }}>Times in {timezone} · {date}</div></> : slotError ? null : <div role="status" aria-label="Loading days"><Skeleton width="100%" height={50} variant="rect" /></div>}
      {err ? <div role="alert" style={{ fontSize: 12.5, color: T.red1 }}>{err}</div> : null}
      {slotError ? <div role="alert" style={{ fontSize: 12.5, color: T.red1 }}>{slotError} <Button onClick={retry}>Retry</Button></div>
        : !slots ? <div role="status" aria-label="Loading availability" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>{Array.from({ length: 6 }, (_, i) => <Skeleton key={i} width="100%" height={48} variant="rect" />)}</div>
        : slots.length === 0 ? <div style={{ fontSize: 12.5, color: T.soft }}>{t("appointments.sheet.noSlots", "No open times for this day — try another day.")}</div> : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(104px, 1fr))", gap: 8 }}>
          {slots.map((s) => (
            /* ui-raw: appointment slot button — SlotPicker's pattern (group 9) */
            <Button variant="secondary" key={s.start + s.staffId} type="button" disabled={saving} aria-busy={saving && savingSlot === s} onClick={() => { setErr(null); setPicked(s); }} aria-pressed={picked === s} data-testid="appt-resched-slot"
              style={{ flexDirection: "column", gap: 2, height: "auto", minHeight: 52, padding: "8px 6px", borderRadius: 8, border: `1.5px solid ${picked === s ? T.p1 : T.n5}`, background: picked === s ? T.p4 : T.white, cursor: "pointer", fontFamily: T.font, opacity: saving ? 0.6 : 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: T.n1 }}>{saving && savingSlot === s ? "Saving…" : s.start}</div>
              <div style={{ fontSize: 10.5, color: T.soft, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.staffName}</div>
            </Button>
          ))}
        </div>
      )}
      {picked && slots?.includes(picked) ? (
        <div role="group" aria-label="Confirm the new time" data-testid="appt-resched-confirm"
          style={{ display: "flex", flexDirection: "column", gap: 10, padding: 12, borderRadius: 10, border: `1px solid ${T.n5}`, background: T.n6 }}>
          <div style={{ fontSize: 13, color: T.n1, lineHeight: 1.5 }}>
            Move {appt.customer.name} to <strong>{date} {picked.start}</strong>{picked.staffName ? <> with {picked.staffName}</> : null}?
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
            <Button variant="secondary" type="button" disabled={saving} onClick={() => setPicked(null)} data-testid="appt-resched-confirm-cancel">Cancel</Button>
            <Button variant="primary" type="button" disabled={saving} aria-busy={saving} onClick={() => { void pickSlot(picked); }} data-testid="appt-resched-confirm-move">
              {saving ? "Saving…" : "Move appointment"}
            </Button>
          </div>
        </div>
      ) : null}
    </Drawer>
  );
}

/* ── Attribution ─────────────────────────────────────────────────────────── */
function SourceCell({ appt }: { appt: Appointment }) {
  const t = useT();
  const via = VIA_LABEL[appt.bookedVia || appt.origin || "staff"] || appt.bookedVia || "—";
  const at = appt.attribution;
  return (
    <span style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
      <span style={{ minWidth: 0 }}>
        <span style={{ display: "block", fontSize: 12.5, color: T.n2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{via}</span>
        {appt.bookedFrom ? (
          <span style={{ display: "block", fontSize: 11, color: T.soft, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 170 }}>{appt.bookedFrom}</span>
        ) : null}
      </span>
      {at ? (
        <Tooltip position="top" content={
          <span style={{ display: "flex", flexDirection: "column", gap: 2, textAlign: "left" }}>
            <span style={{ fontWeight: 700 }}>{t("appointments.book.attribution", "Attribution")}</span>
            <span>{t("appointments.book.text3", "utm_id:")}{at.id || "—"}</span>
            <span>{t("appointments.book.text4", "utm_source:")}{at.source || "—"}</span>
            <span>{t("appointments.book.text5", "utm_medium:")}{at.medium || "—"}</span>
            <span>{t("appointments.book.text6", "utm_campaign:")}{at.campaign || "—"}</span>
            <span>{t("appointments.book.text7", "utm_content:")}{at.content || "—"}</span>
            <span style={{ opacity: 0.75, marginTop: 2 }}>{t("appointments.book.openForMore", "Open the appointment for the full source")}</span>
          </span>
        }>
          <span tabIndex={0} aria-label={t("appointments.book.attribution", "Attribution")}
            style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 20, height: 20, borderRadius: 999, background: T.p4, color: T.p1, cursor: "help", flexShrink: 0 }}>
            <Icon.link size={11} />
          </span>
        </Tooltip>
      ) : null}
    </span>
  );
}


/* ── Screen ──────────────────────────────────────────────────────────────── */
/* The server's own reason for a refused state change (CC 0266), e.g. "No-show can only be set
   after the appointment start time", instead of the generic line. */
const actionFailTitle = (e: unknown, fallback: string) => {
  const err = e as { code?: string; message?: string } | null;
  return err?.code === "INVALID_STATE" && err.message ? err.message : fallback;
};
/** The appointment's start as an instant on its own clock (CC 0266), or null when its zone or
 *  start is unknown. */
const startInstantMs = (date?: string | null, start?: string | null, zone?: string | null): number | null => {
  if (!date || !start || !zone) return null;
  const iso = wallClockToIso(date, String(start).slice(0, 5), zone);
  const ms = iso ? new Date(iso).getTime() : NaN;
  return Number.isFinite(ms) ? ms : null;
};
/** Whether the appointment's start has passed on its own clock (CC 0266). The server refuses
 *  No-show before it. An unknown zone or start keeps No-show hidden. */
const startHasPassed = (date?: string | null, start?: string | null, zone?: string | null) => {
  const ms = startInstantMs(date, start, zone);
  return ms !== null && Date.now() >= ms;
};
/** setTimeout's longest delay (about 24.8 days); a start further out re-arms after it. */
const MAX_TIMER_MS = 2_147_483_647;

export default function AppointmentsBook() {
  const t = useT();
  const nav = useNav();
  const isMobile = useIsMobile();
  const canBook = can("appointments.book");
  const canCheckin = can("appointments.checkin");
  /* Export needs reports.export on top of the list's read (CC 0066); without it the button is not drawn. */
  const canExport = can("reports.export");
  const [exporting, setExporting] = useState(false);
  const [horizon, setHorizon] = useState(30);
  /* The location the user CHOSE. null = not chosen: the read omits locationId
     and the server resolves the default. The resolved default is read from the
     response and never written back here, so it cannot trigger a second read. */
  const [locationId, setLocationId] = useState<string | null>(null);
  /* The last answer that came back, together with the query that produced it.
     They are set as one value, so the export (the answer's bounds plus that
     query's filters) always describes the rows on screen, never a filter edit
     whose read has not come back or has failed. */
  const [book, setBook] = useState<{ response: AppointmentsListResponse; params: ListAppointmentsParams } | null>(null);
  const data = book?.response ?? null;
  /* A failed read before any rows have loaded: LoadState shows it with Retry. */
  const [loadErr, setLoadErr] = useState<string | null>(null);
  /* A failed read while rows are on screen (a re-read of the same query, or a
     new range or filter): the rows stay, and this says they may be out of date. */
  const [staleErr, setStaleErr] = useState<string | null>(null);
  const [reading, setReading] = useState(false);
  const hasData = useRef(false);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [selected, setSelected] = useState<Record<string, string[]>>({});
  const [sort, setSort] = useState<{ key: "time" | "customer" | "status"; dir: 1 | -1 }>({ key: "time", dir: 1 });
  const [busyId, setBusyId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [resched, setResched] = useState<Appointment | null>(null);
  /* Latest-request token: a response that is not for the newest read is dropped. */
  const requestSeq = useRef(0);
  useEffect(() => () => { requestSeq.current += 1; }, []);

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedQuery(query.trim()), QUERY_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [query]);

  /* One forward-looking list (operator, 2026-08-06), fetched as ONE read with
     the horizon, the location scope, the selected facets and the debounced
     search. The horizon goes as `horizonDays`; the server resolves its first
     day from the company's own today. Sort, page and row clicks are not
     inputs, so they never refetch. */
  const listParams = useCallback((): ListAppointmentsParams => {
    const params: ListAppointmentsParams = {
      horizonDays: horizon,
      locationId,
      q: debouncedQuery || null,
    };
    for (const [facetId, values] of Object.entries(selected)) {
      const key = FACET_PARAM[facetId as ApptFacetId];
      if (key && values.length) (params as Record<string, unknown>)[key] = values;
    }
    return params;
  }, [horizon, locationId, debouncedQuery, selected]);
  const refresh = useCallback(() => {
    const seq = ++requestSeq.current;
    const params = listParams();
    /* With nothing on screen (the first read, or Retry after it failed) the
       error is cleared so LoadState shows its skeleton while this read runs. */
    if (!hasData.current) setLoadErr(null);
    setReading(true);
    listAppointments(params)
      .then((r) => {
        if (seq !== requestSeq.current) return;
        hasData.current = true;
        setBook({ response: r, params });
        setLoadErr(null);
        setStaleErr(null);
      })
      .catch((e) => {
        if (seq !== requestSeq.current) return;
        const message = e instanceof Error ? e.message : String(e);
        if (hasData.current) setStaleErr(message); else setLoadErr(message);
      })
      .finally(() => { if (seq === requestSeq.current) setReading(false); });
  }, [listParams]);
  useEffect(() => { refresh(); }, [refresh]);

  /* The company's day can turn while this sits in a background tab, so the
     horizon is re-resolved when the window comes back. Current rows stay up. */
  useEffect(() => {
    let last = 0;
    const onWake = () => {
      if (document.visibilityState !== "visible") return;
      const now = Date.now();
      if (now - last < WAKE_REFRESH_MIN_MS) return;
      last = now;
      refresh();
    };
    window.addEventListener("focus", onWake);
    document.addEventListener("visibilitychange", onWake);
    return () => {
      window.removeEventListener("focus", onWake);
      document.removeEventListener("visibilitychange", onWake);
    };
  }, [refresh]);

  /* Export (CC 0066): the CSV for the rows ON SCREEN — the window the shown
     answer named and the filters of the read that produced it, never a pending
     filter edit or a fresh horizon resolution. Saved under a name we choose:
     the API is cross-origin and exposes neither content-disposition nor the
     row-count header, so the count is taken from the text. Nothing is reported
     as downloaded before the awaited read resolves. */
  const runExport = useCallback(async () => {
    if (exporting || !book) return;
    const params = apptExportParams(book.params, book.response);
    setExporting(true);
    try {
      const epoch = getSessionEpoch(); // a file finished after a sign-out or workspace switch is not downloaded
      const csv = await exportAdminAppointments(params);
      if (epoch !== getSessionEpoch()) return;
      let rows = 0, inQuote = false;
      /* Rows, not lines: a quoted cell may hold a line break. */
      for (let i = 0; i < csv.length; i++) {
        const ch = csv[i];
        if (ch === '"') inQuote = !inQuote;
        else if (ch === "\n" && !inQuote) rows++;
      }
      if (csv.length && !csv.endsWith("\n")) rows++;
      rows = Math.max(0, rows - 1);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = `appointments-${params.dateFrom}-to-${params.dateTo}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(href), 1000);
      toast({ tone: "success", title: t("appointments.book.exported", "Export downloaded"), subtitle: t("appointments.book.exportedCount", "{count, plural, one {# appointment} other {# appointments}}", { count: rows }) });
    } catch (e) {
      const err = e as { code?: string; status?: number; message?: string } | null;
      if (err?.code === "APPOINTMENT_EXPORT_FORBIDDEN" || err?.status === 403) {
        toast({ tone: "danger", title: t("appointments.book.exportForbidden", "You don't have access to exports"), subtitle: t("appointments.book.exportForbiddenSub", "Ask an administrator for the reports export permission.") });
      } else {
        toast({ tone: "danger", title: t("appointments.book.exportFailed", "The export could not be downloaded"), subtitle: err?.message || String(e) });
      }
    } finally {
      setExporting(false);
    }
  }, [exporting, book, t]);

  const scopeLocationId = locationId ?? data?.locationId ?? null;
  const locations = data?.locations ?? [];
  const locName = useCallback((id: string) => locations.find((l) => l.id === id)?.name ?? id, [locations]);
  const appts = data?.appointments ?? [];

  /* A row's "Today" is today on the row's own clock (`clockTimeZone`), else in
     the list's zone, never the viewer's browser zone. Both sides are civil-date
     keys, so the row's date-only string is compared, never parsed as an instant. */
  const now = new Date();
  const todayByZone = new Map<string, string>();
  const isRowToday = (a: Appointment) => {
    const zone = a.clockTimeZone || data?.listTimeZone;
    if (!zone) return false;
    let today = todayByZone.get(zone);
    if (today === undefined) {
      today = calendarDateKey(now, zone);
      todayByZone.set(zone, today);
    }
    return today === a.date;
  };

  /* Facets come from the response and are counted over the UNFILTERED scope
     (range + location), so picking one value never makes the others vanish.
     Staff and service values are IDs with a human label; the rest are raw. */
  const facets: FilterFacet[] = useMemo(() => {
    const src = data?.facets ?? {};
    const opts = (id: ApptFacetId, label: (o: ApptFacetOption) => string) =>
      (src[id] ?? [])
        .map((o) => ({ value: o.value, label: label(o), count: o.count }))
        .sort((x, y) => y.count - x.count || x.label.localeCompare(y.label));
    const raw = (o: ApptFacetOption) => o.label || o.value;
    const out: FilterFacet[] = [
      { id: "status", label: t("appointments.book.status", "Status"), icon: <Icon.check size={13} />, options: opts("status", (o) => STATUS_META[o.value as ApptStatus]?.label ?? raw(o)) },
      { id: "staff", label: t("appointments.book.staff", "Staff"), icon: <Icon.user size={13} />, options: opts("staff", raw) },
      { id: "service", label: t("appointments.book.service", "Service"), icon: <Icon.tags size={13} />, options: opts("service", raw) },
      { id: "channel", label: t("appointments.book.channel", "Channel"), icon: <Icon.video size={13} />, options: opts("channel", (o) => CHANNEL_LABEL[o.value] ?? raw(o)) },
      { id: "via", label: t("appointments.book.source", "Source"), icon: <Icon.link size={13} />, options: opts("via", (o) => VIA_LABEL[o.value] ?? raw(o)) },
      { id: "campaign", label: t("appointments.book.campaign", "Campaign"), icon: <Icon.chart size={13} />, options: opts("campaign", raw) },
      { id: "question", label: t("appointments.book.answeredQuestion", "Answered question"), icon: <Icon.help size={13} />, options: opts("question", raw) },
    ];
    const locOptions = opts("location", (o) => (o.label && o.label !== o.value ? o.label : locName(o.value)));
    if (locOptions.length > 1) out.splice(4, 0, { id: "location", label: t("appointments.book.location", "Location"), icon: <Icon.loc size={13} />, options: locOptions });
    return out.filter((f) => f.options.length > 1 || (selected[f.id]?.length ?? 0) > 0);
  }, [data, locName, selected, t]);

  const rows = useMemo(() => {
    const cmp = (a: Appointment, b: Appointment) => {
      if (sort.key === "customer") return a.customer.name.localeCompare(b.customer.name);
      if (sort.key === "status") return (STATUS_META[a.status] ? t(STATUS_META[a.status].labelKey, STATUS_META[a.status].label) : "").localeCompare(STATUS_META[b.status] ? t(STATUS_META[b.status].labelKey, STATUS_META[b.status].label) : "");
      return (a.date + a.start).localeCompare(b.date + b.start) || a.customer.name.localeCompare(b.customer.name);
    };
    return [...appts].sort((a, b) => cmp(a, b) * sort.dir);
  }, [appts, sort]);

  const filteredTotal = data?.total ?? rows.length;
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const pageNo = Math.min(page, totalPages);
  const pageRows = useMemo(() => rows.slice((pageNo - 1) * pageSize, pageNo * pageSize), [rows, pageNo, pageSize]);
  /* No-show appears in a row's menu the moment its start passes (CC 0271): one timer for the
     earliest upcoming start among the open rows on screen (plus a second), re-armed after it
     fires. The re-render rebuilds every row menu, an open one included. */
  const [clockTick, setClockTick] = useState(0);
  useEffect(() => {
    const now = Date.now();
    let next: number | null = null;
    for (const a of pageRows) {
      if (!OPEN_STATUSES.includes(a.status)) continue;
      const startMs = startInstantMs(a.date, a.start, a.clockTimeZone || data?.listTimeZone);
      if (startMs !== null && startMs > now && (next === null || startMs < next)) next = startMs;
    }
    if (next === null) return;
    const handle = setTimeout(() => setClockTick((n) => n + 1), Math.min(next - now + 1000, MAX_TIMER_MS));
    return () => clearTimeout(handle);
  }, [pageRows, data?.listTimeZone, clockTick]);
  const firstShown = rows.length ? (pageNo - 1) * pageSize + 1 : 0;
  const lastShown = Math.min(pageNo * pageSize, rows.length);
  // Any change to what is being listed returns to page 1 — page 4 of a filter
  // that now matches six rows is an empty table for no reason.
  useEffect(() => { setPage(1); }, [debouncedQuery, selected, horizon, locationId, sort, pageSize]);

  /* Tiles describe the unfiltered scope. A response without `summary` (a
     legacy one-day answer) falls back to counting the loaded rows. */
  const kpi = useMemo(() => data?.summary ?? ({
    total: appts.length,
    checkedIn: appts.filter((a) => a.status === "checked-in").length,
    served: appts.filter((a) => a.status === "served").length,
    noShow: appts.filter((a) => a.status === "no-show").length,
  }), [data, appts]);

  const act = async (id: string, fn: () => Promise<Appointment>, okMsg: string) => {
    setBusyId(id);
    try { await fn(); toast({ tone: "success", title: okMsg }); refresh(); }
    catch (e) { const fb = t("appointments.book.actionFailed", "Couldn't update the appointment"); const title = actionFailTitle(e, fb); toast({ tone: "danger", title, subtitle: title === fb ? (e instanceof Error ? e.message : String(e)) : undefined }); }
    finally { setBusyId(null); }
  };

  const chooseLocation = (id: string | null) => { if (id !== scopeLocationId) setLocationId(id); };

  const toggleFacet = (facetId: string, value: string) => {
    if (facetId === "location") { chooseLocation(value); return; }
    setSelected((prev) => {
      const cur = prev[facetId] ?? [];
      const next = cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value];
      const out = { ...prev, [facetId]: next };
      if (!next.length) delete out[facetId];
      return out;
    });
  };

  const clearFilters = () => { setSelected({}); setQuery(""); setDebouncedQuery(""); };

  const viewAsCustomer = async (id: string) => {
    const r = await openManagePreview(id);
    if (r === "unavailable") toast({ tone: "warn", title: "The customer's page isn't available on this workspace" });
    else if (r === "error") toast({ tone: "warn", title: "Couldn't open the customer's page", subtitle: "Try again." });
  };
  const rowMenu = (a: Appointment) => {
    const open = OPEN_STATUSES.includes(a.status);
    const busy = busyId === a.id;
    const items: Array<{ label: string; icon?: ReactNode; onClick?: () => void; danger?: boolean; testid?: string } | "divider"> = [
      { label: t("appointments.book.viewDetails", "View details"), icon: <Icon.eye size={14} />, onClick: () => nav.go("appointment.detail", { id: a.id }), testid: "appt-act-detail" },
    ];
    /* The customer's manage page as a read-only staff preview, in a new tab. */
    if (canViewAsCustomer()) items.push({ label: "View as customer", icon: <Icon.external size={14} />, onClick: () => { void viewAsCustomer(a.id); }, testid: "appt-act-preview" });
    if (!busy && canCheckin && open) items.push({ label: t("appointments.book.checkIn", "Check in"), icon: <Icon.check size={14} />, onClick: () => { void act(a.id, () => checkInAppointment(a.id), t("appointments.book.checkedInToast", "Checked in")); }, testid: "appt-act-checkin" });
    if (!busy && canCheckin && a.status === "checked-in") items.push({ label: t("appointments.book.markServed", "Mark served"), icon: <Icon.check size={14} />, onClick: () => { void act(a.id, () => checkInAppointment(a.id), t("appointments.book.servedToast", "Marked served")); }, testid: "appt-act-served" });
    if (!busy && canCheckin && open && startHasPassed(a.date, a.start, a.clockTimeZone || data?.listTimeZone)) items.push({ label: t("appointments.book.noShow", "No-show"), icon: <Icon.warn size={14} />, onClick: () => { void act(a.id, () => markAppointmentNoShow(a.id), t("appointments.book.noShowToast", "Recorded no-show")); }, testid: "appt-act-noshow" });
    if (!busy && canBook && open) {
      items.push("divider");
      items.push({ label: t("appointments.book.reschedule", "Reschedule"), icon: <Icon.calendar size={14} />, onClick: () => setResched(a), testid: "appt-act-resched" });
      items.push({ label: t("appointments.book.cancel", "Cancel appointment"), icon: <Icon.x size={14} />, danger: true, onClick: () => { void act(a.id, () => cancelAppointment(a.id), t("appointments.book.cancelledToast", "Appointment cancelled")); }, testid: "appt-act-cancel" });
    }
    return items;
  };


  const th = (key: "time" | "customer" | "status", label: string, extra?: CSSProperties) => (
    <th style={{ ...thBase, ...extra }}>
      {/* ui-raw: sortable table header — Table/DataTable owns sorting (group 7) */}
      <button type="button" onClick={() => setSort((s) => ({ key, dir: s.key === key && s.dir === 1 ? -1 : 1 }))}
        style={{ display: "inline-flex", alignItems: "center", gap: 4, border: "none", background: "transparent", padding: 0, cursor: "pointer", font: "inherit", color: "inherit", textTransform: "inherit", letterSpacing: "inherit" }}>
        {label}{sort.key === key ? <Icon.chev size={11} dir={sort.dir === 1 ? "down" : "up"} /> : null}
      </button>
    </th>
  );

  return (
    <Screen rail="appt-book">
      <PageHeader
        title={t("appointments.book.appointments", "Appointments")}
        subtitle={t("appointments.book.subtitle", "Every upcoming appointment — check in, reschedule and record outcomes.")}
        breadcrumb={null}
        actions={(canExport || canBook) ? (
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {canExport ? (
              <Button variant="secondary" onClick={() => { void runExport(); }} disabled={exporting || !book} aria-busy={exporting} data-testid="appt-export"
                title={t("appointments.book.exportHint", "Download the appointments this list shows, with the booking-question answers, as a CSV file.")}>
                <Icon.download size={14} /> {exporting ? t("appointments.book.exporting", "Exporting…") : t("appointments.book.export", "Export")}
              </Button>
            ) : null}
            {canBook ? <Button onClick={() => setSheetOpen(true)} data-testid="appt-book-cta"><Icon.plus size={14} /> {t("appointments.book.bookAppointment", "Book appointment")}</Button> : null}
          </div>
        ) : null}
      />
      <div style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        <div style={{ padding: "14px 24px 0", display: "grid", gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(4, 1fr)", gap: 12 }}>
          <StatTile label={t("appointments.book.upcomingTotal", "Upcoming")} value={kpi.total} icon={<Icon.calendar size={14} />} accent={T.p1} />
          <StatTile label={t("appointments.book.checkedIn", "Checked in")} value={kpi.checkedIn} icon={<Icon.check size={14} />} accent={T.green1} />
          <StatTile label={t("appointments.book.served", "Served")} value={kpi.served} icon={<Icon.user size={14} />} accent={T.n2} />
          <StatTile label={t("appointments.book.noShows", "No-shows")} value={kpi.noShow} icon={<Icon.warn size={14} />} accent={T.orange1} />
        </div>

        <FilterToolbar
          testid="appt-book-filters"
          query={query} onQueryChange={setQuery}
          searchPlaceholder={t("appointments.book.searchPlaceholder", "Search customer, staff, reference, answers, campaign…")}
          facets={facets} selected={selected}
          onToggle={toggleFacet}
          onClearFacet={(id) => { if (id === "location") { chooseLocation(ALL_LOCATIONS); return; } setSelected((p) => { const o = { ...p }; delete o[id]; return o; }); }}
          onClearAll={clearFilters}
          leading={<>
            <Dropdown
              aria-label={t("appointments.book.range", "Range")}
              testid="appt-book-range"
              value={String(horizon)}
              onChange={(v) => setHorizon(Number(v))}
              style={{ width: 176 }}
              options={[
                { value: "7", label: t("appointments.book.next7", "Next 7 days") },
                { value: "30", label: t("appointments.book.next30", "Next 30 days") },
                { value: "90", label: t("appointments.book.next90", "Next 90 days") },
              ]}
            />
            {locations.length > 1 ? (
            <Dropdown
              aria-label={t("appointments.book.location", "Location")}
              testid="appt-book-location"
              value={scopeLocationId}
              onChange={(v) => chooseLocation(v)}
              style={{ width: 260 }}
              options={[
                { value: ALL_LOCATIONS, label: t("appointments.book.allLocations", "All locations"), supportingText: t("appointments.book.acrossTheTenant", "Every boutique, branch or campus") },
                ...locations.map((l) => ({ value: l.id, label: l.name })),
              ]}
            />
            ) : locations.length === 1 ? (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600, color: T.n1 }}><Icon.loc size={13} />{locations[0].name}</span>
            ) : null}
          </>}
        />

        <div style={{ padding: "0 24px 24px" }}>
          {/* The window the shown answer covers and the company zone it is a span
              of days in, as returned; never re-derived here. Each bound is a civil
              date read at UTC noon and formatted in UTC, so the workspace's date
              format applies and no display or viewer zone can move the day. */}
          {data ? (
            <div data-testid="appt-book-window" style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 10, fontSize: 12, color: T.soft }}>
              <Icon.calendar size={12} />
              <span>{t("appointments.book.window", "{from} – {to} · {zone} calendar", { from: friendlyDateShort(`${data.dateFrom}T12:00:00Z`, "UTC"), to: friendlyDateShort(`${data.dateTo}T12:00:00Z`, "UTC"), zone: data.listTimeZone })}</span>
              <span role="status" aria-live="polite">{reading ? t("appointments.book.refreshing", "Updating…") : ""}</span>
            </div>
          ) : null}
          {data && staleErr ? (
            <div role="alert" data-testid="appt-book-refresh-error" style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 10, fontSize: 12.5, color: T.red1 }}>
              <Icon.warn size={14} />
              <span>{t("appointments.book.refreshFailed", "Couldn't update the list — showing the last loaded appointments.")}</span>
              <Button variant="secondary" onClick={() => refresh()} disabled={reading} aria-busy={reading} data-testid="appt-book-refresh-retry">
                {reading ? t("appointments.book.retrying", "Retrying…") : t("appointments.book.retry", "Retry")}
              </Button>
            </div>
          ) : null}
          <LoadState
            loading={!data && !loadErr}
            error={data ? null : loadErr}
            skeleton="table"
            empty={rows.length === 0}
            noun={t("appointments.book.appointmentsNoun", "appointments")}
            onRetry={refresh}
            loadingLabel={t("appointments.book.loading", "Loading the book…")}
            testid="appt-book"
            emptyState={
              kpi.total === 0 ? (
                <EmptyState variant="card" icon={<Icon.calendar size={24} />}
                  title={t("appointments.book.emptyRange", "No appointments in this range.")}
                  body={canBook ? t("appointments.book.emptyDayCta", "Book the first appointment with the button above.") : undefined} />
              ) : (
                <EmptyState variant="card" icon={<Icon.filter size={24} />}
                  title={t("appointments.book.noMatches", "No appointments match these filters")}
                  body={t("appointments.book.noMatchesBody", "The day holds appointments — they are hidden by the current search and filters.")}
                  action={<Button variant="secondary" onClick={clearFilters}>{t("shell.composites.clearAll", "Clear all")}</Button>} />
              )
            }
          >
            <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, width: "fit-content", minWidth: "100%" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: isMobile ? 720 : 980 }}>
                  <thead>
                    <tr style={{ color: T.soft, fontSize: 12, borderBottom: `1px solid ${T.border}` }}>
                      {th("time", t("appointments.book.date", "Date"), { width: 116, borderTopLeftRadius: 9 })}
                      <th style={{ ...thBase, width: 104 }}>{t("appointments.book.time", "Time")}</th>
                      {th("customer", t("appointments.book.customer", "Customer"))}
                      <th style={thBase}>{t("appointments.book.service", "Service")}</th>
                      <th style={thBase}>{t("appointments.book.staff", "Staff")}</th>
                      <th style={thBase}>{t("appointments.book.location", "Location")}</th>
                      <th style={thBase}>{t("appointments.book.source", "Source")}</th>
                      <th style={thBase}>{t("appointments.book.answers", "Answers")}</th>
                      {th("status", t("appointments.book.status", "Status"), { width: 120 })}
                      <th style={{ ...thBase, width: 52, borderTopRightRadius: 9 }} aria-label={t("common.actions.actions", "Actions")} />
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map((a, i) => {
                      const st = STATUS_META[a.status];
                      const answers = a.answers ?? [];
                      return (
                        <tr key={a.id} data-testid="appt-row" onClick={() => nav.go("appointment.detail", { id: a.id })}
                          style={{ borderBottom: i === pageRows.length - 1 ? "none" : `1px solid ${T.border}`, cursor: "pointer", opacity: a.status === "cancelled" ? 0.6 : 1 }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = T.n6; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>
                          <td style={tdStyle}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: T.n1, whiteSpace: "nowrap" }}>
                              {isRowToday(a) ? t("appointments.book.today", "Today") : new Date(a.date + "T00:00:00").toLocaleDateString(undefined, { day: "2-digit", month: "short" })}
                            </div>
                            <div style={{ fontSize: 11, color: T.soft, whiteSpace: "nowrap" }}>{new Date(a.date + "T00:00:00").toLocaleDateString(undefined, { weekday: "short" })}</div>
                          </td>
                          <td style={tdStyle}>
                            <div style={{ fontFamily: T.fontMono, fontSize: 13.5, fontWeight: 700, color: T.n1 }}>{a.start}</div>
                            <div style={{ fontSize: 11, color: T.soft, whiteSpace: "nowrap" }}>{a.end} · {minutesOf(a)} {t("appointments.book.text8", "min")}</div>
                          </td>
                          <td style={tdStyle}>
                            <div style={{ fontSize: 13.5, fontWeight: 700, color: T.n1 }}>{a.customer.name}</div>
                            <div style={{ fontSize: 11.5, color: T.soft, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 210 }}>
                              {a.customer.email || a.customer.phone || a.ref}
                            </div>
                          </td>
                          <td style={tdStyle}>
                            <div style={{ color: T.n1 }}>{a.service}</div>
                            <div style={{ fontSize: 11.5, color: T.soft }}>{a.channel ? <>{CHANNEL_LABEL[a.channel] ?? a.channel} · </> : null}<span style={{ fontFamily: T.fontMono }}>{a.ref}</span></div>
                          </td>
                          <td style={tdStyle}>{a.staffName ?? t("appointments.book.anyStaff", "Any staff")}</td>
                          <td style={tdStyle}>{a.locationName ?? locName(a.locationId)}</td>
                          <td style={tdStyle}><SourceCell appt={a} /></td>
                          <td style={tdStyle}>
                            {answers.length === 0 ? <span style={{ color: T.soft }}>—</span> : (
                              <Tooltip position="top" content={
                                <span style={{ display: "flex", flexDirection: "column", gap: 3, textAlign: "left", maxWidth: 280 }}>
                                  {answers.slice(0, 4).map((x, i) => (
                                    <span key={i}><span style={{ opacity: 0.72 }}>{x.question}: </span>{x.answer}</span>
                                  ))}
                                  {answers.length > 4 ? <span style={{ opacity: 0.72 }}>+{answers.length - 4} {t("appointments.book.more", "more")}</span> : null}
                                </span>
                              }>
                                <span tabIndex={0} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 9px", borderRadius: 999, background: T.p4, color: T.p1, fontSize: 11.5, fontWeight: 600, cursor: "help" }}>
                                  <Icon.help size={11} />{answers.length}
                                </span>
                              </Tooltip>
                            )}
                          </td>
                          <td style={tdStyle}>
                            <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700, color: st.fg, background: st.bg, whiteSpace: "nowrap" }}>{t(st.labelKey, st.label)}</span>
                          </td>
                          <td style={{ ...tdStyle, paddingLeft: 0, paddingRight: 10 }} onClick={(e) => e.stopPropagation()}>
                            <RowActions testid="appt-row" moreHeader={a.ref} moreItems={rowMenu(a)} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", padding: "10px 14px", borderTop: `1px solid ${T.border}` }}>
                <span style={{ fontSize: 12.5, color: T.soft }}>
                  {t("appointments.book.showing", "Showing")} {firstShown}–{lastShown} {t("appointments.book.of", "of")} {filteredTotal}
                </span>
                <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 12.5, color: T.soft }}>
                    {t("appointments.book.rowsPerPage", "Rows per page")}
                    <Dropdown aria-label={t("appointments.book.rowsPerPage", "Rows per page")} testid="appt-book-pagesize"
                      value={String(pageSize)} onChange={(v) => setPageSize(Number(v))}
                      options={[10, 25, 50, 100].map((n) => ({ value: String(n), label: String(n) }))}
                      style={{ width: 92 }} />
                  </span>
                  {totalPages > 1 ? (
                    <Pagination totalPages={totalPages} currentPage={pageNo} onPageChange={setPage} size="sm" showStatus={false}
                      aria-label={t("appointments.book.pagination", "Appointment pages")} />
                  ) : null}
                </div>
              </div>
            </div>
          </LoadState>
        </div>

        <div style={{ padding: "0 24px 24px" }}>
          <DataSourceStrip screen="appt-book" ops={["listAppointments", "bookAppointment", "rescheduleAppointment", "cancelAppointment", "checkInAppointment", "markAppointmentNoShow", "listApptSlots", "listAppointmentServices"]} />
        </div>
      </div>

      <BookAppointmentSheet open={sheetOpen} onClose={() => setSheetOpen(false)} locationId={scopeLocationId === ALL_LOCATIONS ? null : scopeLocationId} origin="book" onBooked={() => refresh()} />
      {resched ? <RescheduleDrawer appt={resched} onClose={() => setResched(null)} onDone={(a) => { setResched(null); toast({ tone: "success", title: t("appointments.book.rescheduledToast", "Rescheduled"), subtitle: `${a.date} · ${a.start}` }); refresh(); }} /> : null}
    </Screen>
  );
}
